import { useEffect, useState, Fragment } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import { debounce } from "lodash";
import {
	Button,
	Col,
	Drawer,
	Form,
	Input,
	Row,
	Select,
	Spin,
	TreeSelect,
} from "antd";
import PropTypes from "prop-types";

import { Modals, Types } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";
import Rules from "scripts/rules";

import Upload from "components/Upload";
import { ObjectNumbersToEnglish } from "scripts/helpers";

const MapEvent = ({ onChange }) => {
	const map = useMap();

	map.on("moveend", () => {
		const { lat, lng } = map.getCenter();

		onChange({ latitude: lat, longitude: lng, zoom: map.getZoom() });
	});

	return null;
};

const Component = ({
	data = {},
	onUpdate,
	options: { categories = [], cities = [] },
}) => {
	const initialState = {
		users: [],
	};
	const [loaded, setLoaded] = useState();
	const [state, setState] = useState(initialState);
	const { loading, submitting, users, visible } = state;
	const { title, slug } = data;
	const { hash, pathname, search } = useLocation();
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const handleChange = ({ latitude, longitude }) => {
		form.setFieldsValue({
			latitude: latitude.toString(),
			longitude: longitude.toString(),
		});
	};

	const handleSearch = debounce((search) => {
		if (search?.length > 1) {
			API.user
				.list({ paginate: false, search })
				.then(({ data }) => {
					setState((prevState) => ({
						...prevState,
						users: data.map(({ id, firstName, lastName }) => ({
							value: id,
							label: `${firstName} ${lastName}`,
						})),
					}));
				})
				.catch(() => {});
		}
	}, 500);

	const handleSubmit = () => {
		if (!submitting) {
			form.validateFields().then((values) => {
				values.avatarId = values.avatar?.id || 977;
				values.coverId = values.cover?.id || 117;

				values = ObjectNumbersToEnglish(values);

				delete values.avatar;
				delete values.cover;

				const action =
					hash === `#${Modals.CREATE}`
						? API.job.create(values)
						: API.job.update(slug, values);

				setState((prevState) => ({ ...prevState, submitting: true }));

				action
					.then(() => {
						onUpdate(null, true);
						navigate(-1);
					})
					.catch(() => {
						setState((prevState) => ({
							...prevState,
							submitting: false,
						}));
					});
			});
		}
	};

	const componentDidUpdate = () => {
		switch (hash) {
			case `#${Modals.CREATE}`: {
				setState((prevState) => ({ ...prevState, visible: true }));

				setTimeout(() => {
					setLoaded(true);
				}, 0);
				break;
			}
			case `#${Modals.EDIT}`: {
				if (slug) {
					setState((prevState) => ({
						...prevState,
						visible: true,
						loading: true,
					}));

					API.job
						.details(slug)
						.then(({ data }) => {
							setLoaded(true);

							setState((prevState) => ({
								...prevState,
								loading: false,
								users: [{ value: data.user?.id, label: data.user?.fullName }],
							}));

							form.setFieldsValue({
								...data,
								cityId: data.city?.id,
								categoryId: data.category?.id,
								userId: data.user?.id,
							});
						})
						.catch(() => {
							setState((prevState) => ({ ...prevState, loading: false }));
						});
				} else {
					navigate(`${pathname}${search}`, { replace: true });
				}

				break;
			}
			default: {
				if (visible) form.resetFields();

				setState(initialState);

				break;
			}
		}
	};

	useEffect(componentDidUpdate, [hash]);

	return (
		<Drawer
			className="sp-form-drawer"
			footer={
				<>
					<Button
						type="primary"
						onClick={handleSubmit}
						loading={submitting}
					>
						{Resources.confirm}
					</Button>
					<Button
						onClick={() => navigate(-1)}
						loading={submitting}
					>
						{Resources.cancel}
					</Button>
				</>
			}
			onClose={() => navigate(-1)}
			title={
				slug
					? `${Resources.edit} ${Resources.job} "${title || "-"}"`
					: `${Resources.add} ${Resources.job}`
			}
			maskClosable={false}
			open={visible}
			width={768}
		>
			<Form
				layout="vertical"
				form={form}
			>
				{loading ? (
					<Spin className="sp-drawer-spin" />
				) : (
					<Row gutter={[16, 8]}>
						<Col xs={12}>
							<Form.Item
								name="title"
								label={Resources.title}
								rules={Rules.required}
							>
								<Input />
							</Form.Item>
						</Col>
						<Col xs={12}>
							<Form.Item
								name="ceo"
								label={`${Resources.name} ${Resources.admin}`}
								rules={Rules.required}
							>
								<Input />
							</Form.Item>
						</Col>
						<Col xs={12}>
							<Form.Item
								name="workTime"
								label={Resources.workTime}
								rules={Rules.required}
							>
								<Input />
							</Form.Item>
						</Col>
						<Col xs={12}>
							<Form.Item
								name="discount"
								label={Resources.discount}
							>
								<Input
									type="number"
									min={0}
									max={100}
								/>
							</Form.Item>
						</Col>
						<Col xs={12}>
							<Form.Item
								name="userId"
								label={Resources.user}
								rules={Rules.required}
							>
								<Select
									options={users}
									onSearch={handleSearch}
									filterOption={false}
									showArrow={false}
									showSearch
								/>
							</Form.Item>
						</Col>

						<Col xs={12}>
							<Form.Item
								name="isPublished"
								label={`${Resources.status} ${Resources.show}`}
								rules={Rules.required}
							>
								<Select
									options={[
										{ value: true, label: Resources.visible },
										{ value: false, label: Resources.invisible },
									]}
								/>
							</Form.Item>
						</Col>
						<Col xs={12}>
							<Form.Item
								name="categoryId"
								label={Resources.category}
								rules={Rules.required}
							>
								<TreeSelect
									treeData={categories}
									treeNodeFilterProp="label"
									showSearch
								/>
							</Form.Item>
						</Col>
						<Col xs={12}>
							<Form.Item
								name="cityId"
								label={Resources.city}
								rules={Rules.required}
							>
								<Select
									options={cities}
									optionFilterProp="label"
									showSearch
								/>
							</Form.Item>
						</Col>
						<Col xs={24}>
							<Row gutter={[16, 8]}>
								<Form.List name="phoneNumbers">
									{(fields, { add, remove }) => (
										<>
											{fields.map(({ key, name, ...restField }) => (
												<Fragment key={key}>
													<Col xs={12}>
														<Form.Item
															name={[name, "number"]}
															label={Resources.number}
															rules={Rules.required}
															{...restField}
														>
															<Input className="sp-input-ltr" />
														</Form.Item>
													</Col>
													<Col xs={10}>
														<Form.Item
															name={[name, "type"]}
															label={Resources.type}
															rules={Rules.required}
															{...restField}
														>
															<Select
																options={[
																	{ value: "mobile", label: Resources.mobile },
																	{ value: "phone", label: Resources.phone },
																	{ value: "fax", label: Resources.fax },
																]}
															/>
														</Form.Item>
													</Col>
													<Col xs={2}>
														<Button
															type="link"
															onClick={() => remove(name)}
															style={{
																marginTop: 32,
																padding: 0,
															}}
															block
														>
															<i className="fa fa-minus-circle" />
														</Button>
													</Col>
												</Fragment>
											))}
											<Col xs={24}>
												<Form.Item>
													<Button
														type="dashed"
														onClick={() => add()}
														block
													>{`${Resources.add} ${Resources.number}`}</Button>
												</Form.Item>
											</Col>
										</>
									)}
								</Form.List>
							</Row>
						</Col>
						<Col xs={24}>
							<Form.Item
								name="address"
								label={Resources.address}
								rules={Rules.required}
							>
								<Input />
							</Form.Item>
						</Col>
						<Col xs={12}>
							<Form.Item
								name="longitude"
								label={Resources.longitude}
							>
								<Input readOnly />
							</Form.Item>
						</Col>
						<Col xs={12}>
							<Form.Item
								name="latitude"
								label={Resources.latitude}
							>
								<Input readOnly />
							</Form.Item>
						</Col>
						<Col xs={24}>
							{loaded && (
								<MapContainer
									center={{
										lat: parseFloat(process.env.REACT_APP_LATITUDE),
										lng: parseFloat(process.env.REACT_APP_LONGITUDE),
									}}
									length={4}
									zoom={parseInt(process.env.REACT_APP_ZOOM)}
									style={{ height: 220, width: "100%" }}
								>
									<MapEvent onChange={handleChange} />
									<TileLayer
										attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
										url="http://{s}.tile.osm.org/{z}/{x}/{y}.png"
									/>
								</MapContainer>
							)}
						</Col>
						<Col xs={24}>
							<Form.Item
								name="description"
								label={Resources.description}
							>
								<Input.TextArea rows={4} />
							</Form.Item>
						</Col>
						<Col xs={12}>
							<Form.Item
								name="websiteUrl"
								label={`${Resources.address} ${Resources.site}`}
							>
								<Input className="sp-input-ltr" />
							</Form.Item>
						</Col>
						<Col xs={12}>
							<Form.Item
								name="instagramUrl"
								label={`${Resources.address} ${Resources.instagram}`}
							>
								<Input className="sp-input-ltr" />
							</Form.Item>
						</Col>
						<Col xs={12}>
							<Form.Item
								name="avatar"
								label={`${Resources.image} ${Resources.profile}`}
							>
								<Upload section={Types.upload.job} />
							</Form.Item>
						</Col>
						<Col xs={12}>
							<Form.Item
								name="cover"
								label={`${Resources.image} ${Resources.banner}`}
							>
								<Upload
									aspect={1.8}
									section={Types.upload.job}
								/>
							</Form.Item>
						</Col>
					</Row>
				)}
			</Form>
		</Drawer>
	);
};

Component.propTypes = {
	data: PropTypes.object,
	onUpdate: PropTypes.func.isRequired,
	options: PropTypes.object.isRequired,
};

export default Component;
